import { useEffect, useState } from "react"
import { IconButton, Stack, Tooltip } from "@mui/material"
import { alpha } from "@mui/material/styles"
import {
  FormatBold,
  FormatItalic,
  FormatUnderlined,
  StrikethroughS,
  Code,
  Link,
  Undo,
  Redo,
} from "@mui/icons-material"
import type Quill from "quill"
import type { EditorRange } from "./sourceTokens"

export interface FormatToolbarProps {
  quill: Quill | null
  onLink: () => void
  getSelection: () => EditorRange | null
  readOnly?: boolean
  valid: boolean
  canUndo: boolean
  canRedo: boolean
}

const formats = [
  { name: "bold", label: "Жирный", Icon: FormatBold },
  { name: "italic", label: "Курсив", Icon: FormatItalic },
  { name: "underline", label: "Подчёркнутый", Icon: FormatUnderlined },
  { name: "strike", label: "Зачёркнутый", Icon: StrikethroughS },
  { name: "code", label: "Код", Icon: Code },
]

export function FormatToolbar({
  quill,
  onLink,
  getSelection,
  readOnly,
  valid,
  canUndo,
  canRedo,
}: FormatToolbarProps) {
  const [active, setActive] = useState<Record<string, unknown>>({})
  useEffect(() => {
    if (!quill) return
    const update = () => {
      const range = quill.getSelection()
      if (range) setActive(quill.getFormat(range))
    }
    quill.on("editor-change", update)
    update()
    return () => {
      quill.off("editor-change", update)
    }
  }, [quill])
  const disabled = readOnly || !valid
  const format = (name: string) => {
    if (disabled || !quill) return
    const range = getSelection() ?? { index: 0, length: 0 }
    const enabled = !quill.getFormat(range)[name]
    quill.setSelection(range, "silent")
    if (range.length)
      quill.formatText(range.index, range.length, name, enabled, "user")
    else quill.format(name, enabled, "user")
    setActive(quill.getFormat(range))
  }
  return (
    <Stack
      direction="row"
      useFlexGap
      flexWrap="wrap"
      role="toolbar"
      aria-label="Форматирование текста"
      sx={{
        p: 0.5,
        borderBottom: 1,
        borderColor: (theme) => alpha(theme.palette.text.primary, 0.14),
      }}
    >
      {[
        {
          name: "undo" as const,
          label: "Отменить действие",
          shortcut: "Ctrl+Z / ⌘Z",
          keys: "Control+z Meta+z",
          enabled: canUndo,
          Icon: Undo,
        },
        {
          name: "redo" as const,
          label: "Повторить действие",
          shortcut: "Ctrl+Shift+Z / Ctrl+Y / ⌘⇧Z / ⌘Y",
          keys: "Control+Shift+z Control+y Meta+Shift+z Meta+y",
          enabled: canRedo,
          Icon: Redo,
        },
      ].map(({ name, label, shortcut, keys, enabled, Icon }) => (
        <Tooltip key={name} title={`${label} (${shortcut})`} disableInteractive>
          <span>
            <IconButton
              size="small"
              aria-label={label}
              aria-keyshortcuts={keys}
              disabled={disabled || !enabled}
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => quill?.history[name]()}
            >
              <Icon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      ))}
      {formats.map(({ name, label, Icon }) => (
        <Tooltip key={name} title={label} disableInteractive>
          <span>
            <IconButton
              size="small"
              aria-label={label}
              aria-pressed={Boolean(active[name])}
              color={active[name] ? "primary" : "default"}
              disabled={disabled}
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => format(name)}
            >
              <Icon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      ))}
      <Tooltip title="Ссылка (Ctrl+K / ⌘K)" disableInteractive>
        <span>
          <IconButton
            size="small"
            aria-label="Ссылка"
            aria-keyshortcuts="Control+k Meta+k"
            disabled={!valid}
            onMouseDown={(event) => event.preventDefault()}
            onClick={onLink}
          >
            <Link fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
    </Stack>
  )
}
